/**
 * minigameStore.js - Zentraler Store für Minispiel-State
 *
 * Nutzt das useSyncExternalStore Pattern für React 18+ Kompatibilität.
 * Vermeidet globale mutable Variablen durch einen sauberen Store-Ansatz.
 *
 * Features:
 * - Typ-sicherer Store mit klarer API
 * - Socket-Listener werden einmalig registriert
 * - Automatische Cleanup-Unterstützung
 * - PlayerIndex wird synchron aus GameContext übernommen
 */

import socketService from '../socketService';
import { MINIGAME_EVENTS } from '../shared/core/minigameConstants';

// =============================================================================
// STORE STATE
// =============================================================================

const store = {
  // Minigame state
  phase: null,          // 'splash' | 'playing' | 'ranking' | null
  minigameData: null,   // Server-Daten für aktuelles Minispiel
  rankings: null,       // Ergebnisse nach Spielende

  // Player info (synchronisiert aus GameContext)
  playerIndex: 0,

  // Sequential mode state
  sequential: {
    currentPlayer: null,    // Index des aktuellen Spielers
    currentPlayerName: '',  // Name des aktuellen Spielers
    turnNumber: 0,          // Aktueller Turn (1-basiert)
    totalTurns: 0,          // Gesamtzahl der Turns
    isMyTurn: false,        // Bin ich dran?
    completedPlayers: [],   // Spieler die bereits gespielt haben
  },

  // Tutorial timing (synchronized from server)
  tutorialStartTime: null,  // Server timestamp when tutorial started
  tutorialDuration: null,   // Tutorial duration in ms

  // Internal
  _listeners: new Set(),
  _socketListenersSetup: false,
  _cachedSnapshot: null, // Cached snapshot for useSyncExternalStore
};

// =============================================================================
// SUBSCRIBER PATTERN (für useSyncExternalStore)
// =============================================================================

/**
 * Update cached snapshot and notify listeners
 * IMPORTANT: useSyncExternalStore compares by reference,
 * so we only create a new object when state actually changes.
 */
function updateSnapshot() {
  store._cachedSnapshot = {
    phase: store.phase,
    minigameData: store.minigameData,
    rankings: store.rankings,
    playerIndex: store.playerIndex,
    sequential: { ...store.sequential },
    tutorialStartTime: store.tutorialStartTime,
    tutorialDuration: store.tutorialDuration,
  };
}

function emitChange() {
  updateSnapshot();
  store._listeners.forEach(listener => listener());
}

// Initialize cached snapshot
updateSnapshot();

/**
 * Subscribe to store changes
 * @param {Function} listener - Callback bei Änderungen
 * @returns {Function} Unsubscribe function
 */
export function subscribe(listener) {
  store._listeners.add(listener);
  return () => store._listeners.delete(listener);
}

/**
 * Get current snapshot of store state
 * IMPORTANT: Returns cached object to prevent infinite re-renders
 * @returns {Object} Current state
 */
export function getSnapshot() {
  return store._cachedSnapshot;
}

/**
 * Server snapshot (für SSR, identisch mit client)
 */
export function getServerSnapshot() {
  return store._cachedSnapshot;
}

// =============================================================================
// ACTIONS
// =============================================================================

/**
 * Set player index (called from GameContext when it changes)
 */
export function setPlayerIndex(index) {
  if (store.playerIndex !== index) {
    store.playerIndex = index;
    emitChange();
  }
}

/**
 * Transition to playing phase (after splash)
 */
export function startPlaying() {
  if (store.phase === 'splash') {
    store.phase = 'playing';
    emitChange();
  }
}

/**
 * Reset store to initial state
 */
export function resetMinigame() {
  store.phase = null;
  store.minigameData = null;
  store.rankings = null;
  store.sequential = {
    currentPlayer: null,
    currentPlayerName: '',
    turnNumber: 0,
    totalTurns: 0,
    isMyTurn: false,
    completedPlayers: [],
  };
  store.tutorialStartTime = null;
  store.tutorialDuration = null;
  emitChange();
}

// =============================================================================
// SOCKET EVENT HANDLERS
// =============================================================================

function handleMinigameStart(data) {
  if (!data?.minigameId || !data?.name) {
    console.warn('[MinigameStore] Invalid start data:', data);
    return;
  }

  console.log('[MinigameStore] Minigame started:', data.minigameId);
  store.minigameData = data;
  store.phase = 'splash';
  store.rankings = null;
  emitChange();
}

function handleMinigameResult(data) {
  if (!Array.isArray(data?.rankings)) {
    console.warn('[MinigameStore] Invalid result data:', data);
    return;
  }

  console.log('[MinigameStore] Minigame result received');
  store.rankings = data.rankings;
  store.phase = 'ranking';
  emitChange();
}

function handleMinigameResume() {
  store.phase = null;
  store.minigameData = null;
  store.rankings = null;
  store.sequential = {
    currentPlayer: null,
    currentPlayerName: '',
    turnNumber: 0,
    totalTurns: 0,
    isMyTurn: false,
    completedPlayers: [],
  };
  store.tutorialStartTime = null;
  store.tutorialDuration = null;
  emitChange();
}

/**
 * Handle state update event - captures tutorial timing for synchronized countdown
 */
function handleStateUpdate(data) {
  // Store tutorial timing if provided (sent when all players complete splash)
  if (data?.tutorialStartTime !== undefined) {
    store.tutorialStartTime = data.tutorialStartTime;
    store.tutorialDuration = data.tutorialDuration;
    emitChange();
  }
}

/**
 * Handle turn start event (sequential minigames)
 */
function handleTurnStart(data) {
  // Im Testmode spielt der Tester ALLE Spieler - immer isMyTurn!
  const isMyTurn = data.isLocalTestMode || data.currentPlayer === store.playerIndex;
  const currentPlayerName = data.playerName || `Spieler ${data.currentPlayer + 1}`;

  console.log(`[MinigameStore] 🎯 handleTurnStart DEBUG:`, {
    'data.currentPlayer': data.currentPlayer,
    'store.playerIndex': store.playerIndex,
    isMyTurn,
    'data.turnNumber': data.turnNumber,
    'data.isLocalTestMode': data.isLocalTestMode,
  });

  store.sequential = {
    currentPlayer: data.currentPlayer,
    currentPlayerName,
    turnNumber: data.turnNumber || 1,
    totalTurns: data.totalTurns || 1,
    isMyTurn,
    completedPlayers: data.completedPlayers || [],
  };

  emitChange();
}

// =============================================================================
// SOCKET LISTENER SETUP
// =============================================================================

/**
 * Setup socket listeners (idempotent - safe to call multiple times)
 */
export function setupSocketListeners() {
  if (store._socketListenersSetup) return;
  store._socketListenersSetup = true;

  console.log('[MinigameStore] Setting up socket listeners');

  socketService.registerListener(MINIGAME_EVENTS.START, handleMinigameStart);
  socketService.registerListener(MINIGAME_EVENTS.STATE_UPDATE, handleStateUpdate);
  socketService.registerListener(MINIGAME_EVENTS.RESULT, handleMinigameResult);
  socketService.registerListener(MINIGAME_EVENTS.RESUME, handleMinigameResume);
  socketService.registerListener(MINIGAME_EVENTS.TURN_START, handleTurnStart);
}

/**
 * Cleanup socket listeners (for testing or hot reload)
 */
export function cleanupSocketListeners() {
  if (!store._socketListenersSetup) return;

  console.log('[MinigameStore] Cleaning up socket listeners');

  socketService.off(MINIGAME_EVENTS.START, handleMinigameStart);
  socketService.off(MINIGAME_EVENTS.STATE_UPDATE, handleStateUpdate);
  socketService.off(MINIGAME_EVENTS.RESULT, handleMinigameResult);
  socketService.off(MINIGAME_EVENTS.RESUME, handleMinigameResume);
  socketService.off(MINIGAME_EVENTS.TURN_START, handleTurnStart);

  store._socketListenersSetup = false;
}

/**
 * Full reset (for testing)
 */
export function resetStore() {
  cleanupSocketListeners();
  store.phase = null;
  store.minigameData = null;
  store.rankings = null;
  store.playerIndex = 0;
  store.sequential = {
    currentPlayer: null,
    currentPlayerName: '',
    turnNumber: 0,
    totalTurns: 0,
    isMyTurn: false,
    completedPlayers: [],
  };
  store.tutorialStartTime = null;
  store.tutorialDuration = null;
  store._listeners.clear();
  updateSnapshot(); // Update cached snapshot after reset
}
